import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, LayoutDashboard, LogOut, User } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import logo from "../assets/logo.png";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const links = [
  { to: "/tours", label: "Tours" },
  { to: "/gallery", label: "Gallery" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dark = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const solid = scrolled || !dark;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solid ? "bg-white/90 backdrop-blur-md border-b border-slate-200 shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <span className="grid place-items-center w-10 h-10 rounded-xl bg-white shadow-sm border border-slate-100 overflow-hidden">
            <img src={logo} alt="Flycher Travels" className="w-full h-full object-contain p-0.5" />
          </span>
          <span className={`font-display font-bold text-lg tracking-tight ${solid ? "text-slate-900" : "text-white"}`}>
            Flycher Travels
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`text-sm font-medium transition-colors ${
                solid ? "text-slate-600 hover:text-coral-600" : "text-white/80 hover:text-white"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className={`flex items-center gap-2 rounded-full pl-1 pr-3 py-1 border ${solid ? "border-slate-200 text-slate-700" : "border-white/30 text-white"}`}>
                  <span className="grid place-items-center w-7 h-7 rounded-full bg-coral-600 text-white text-xs font-semibold">
                    {user.name?.[0]?.toUpperCase()}
                  </span>
                  <span className="text-sm font-medium">{user.name?.split(" ")[0]}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel className="truncate">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {isAdmin && (
                  <DropdownMenuItem onClick={() => navigate("/admin")}>
                    <LayoutDashboard className="w-4 h-4 mr-2" /> Admin Dashboard
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate("/my-trips")}>
                  <User className="w-4 h-4 mr-2" /> My Trips
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link to="/login">
                <Button variant="ghost" className={solid ? "text-slate-700" : "text-white hover:text-white hover:bg-white/10"}>
                  Log in
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-coral-600 hover:bg-coral-700 text-white rounded-full px-5">Sign up</Button>
              </Link>
            </>
          )}
        </div>

        <button className={`md:hidden ${solid ? "text-slate-900" : "text-white"}`} onClick={() => setOpen((o) => !o)}>
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-white border-t border-slate-200 px-5 py-4 space-y-3">
          {links.map((l) => (
            <Link key={l.to} to={l.to} className="block text-slate-700 font-medium py-1">
              {l.label}
            </Link>
          ))}
          <div className="pt-2 border-t border-slate-100 flex flex-col gap-2">
            {user ? (
              <>
                {isAdmin && <Button variant="outline" onClick={() => navigate("/admin")}>Admin Dashboard</Button>}
                <Button variant="outline" onClick={() => navigate("/my-trips")}>My Trips</Button>
                <Button variant="ghost" className="text-red-600" onClick={logout}>Log out</Button>
              </>
            ) : (
              <>
                <Link to="/login"><Button variant="outline" className="w-full">Log in</Button></Link>
                <Link to="/register"><Button className="w-full bg-coral-600 text-white">Sign up</Button></Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
